import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

interface ParentAggregateStats {
  totalActivities: number;
  averageScore: number;
  totalChildren: number;
  totalPoints: number;
  loading: boolean;
}

export function useParentAggregateStats() {
  const [stats, setStats] = useState<ParentAggregateStats>({
    totalActivities: 0,
    averageScore: 0,
    totalChildren: 0,
    totalPoints: 0,
    loading: true,
  });
  const { user } = useAuth();

  const fetchAggregateStats = async () => {
    if (!user) {
      setStats({
        totalActivities: 0,
        averageScore: 0,
        totalChildren: 0,
        totalPoints: 0,
        loading: false,
      });
      return;
    }

    try {
      // Get all children for this parent
      const { data: children, error: childrenError } = await supabase
        .from('children')
        .select('id, total_points')
        .eq('parent_id', user.id);

      if (childrenError) throw childrenError;

      const totalChildren = children?.length || 0;
      const totalPoints = children?.reduce((sum, child) => sum + child.total_points, 0) || 0;

      if (totalChildren === 0) {
        setStats({
          totalActivities: 0,
          averageScore: 0,
          totalChildren: 0,
          totalPoints: 0,
          loading: false,
        });
        return;
      }

      // Get all progress records for all children
      const childIds = children.map(child => child.id);
      const { data: progressData, error: progressError } = await supabase
        .from('child_progress')
        .select('score')
        .in('child_id', childIds);

      if (progressError) throw progressError;

      const totalActivities = progressData?.length || 0;
      const averageScore = totalActivities > 0 
        ? Math.round(progressData.reduce((sum, p) => sum + p.score, 0) / totalActivities)
        : 0;

      setStats({
        totalActivities,
        averageScore,
        totalChildren,
        totalPoints,
        loading: false,
      });
    } catch (error) {
      console.error('Error fetching parent aggregate stats:', error);
      setStats({
        totalActivities: 0,
        averageScore: 0,
        totalChildren: 0,
        totalPoints: 0,
        loading: false,
      });
    }
  };

  useEffect(() => {
    fetchAggregateStats();
  }, [user]);

  return {
    stats,
    loading: stats.loading,
    refetch: fetchAggregateStats,
  };
}